import React, { useEffect, useMemo, useState } from 'react';
import { Search, Instagram, Facebook, Archive, ArrowLeft } from 'lucide-react';
import type { MetaConversation } from '@/lib/stores/metaMessagingStore';
import { conversationLabel, CHANNEL_LABEL } from './metaConversationHelpers';
import { useMetaMessagingStore } from '@/lib/stores/metaMessagingStore';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { useChatRowPrefs } from '@/components/whatsapp2/sidebar/useChatRowPrefs';
import { arrangeMetaConversations, metaChatLike, metaPrefsKey } from './metaListRules';
import { MetaConversationRow } from './MetaConversationRow';
import { useUserData } from '@/lib/auth';

interface MetaConversationListProps {
  channel: 'instagram' | 'messenger';
  conversations: MetaConversation[];
  selectedId: string | null;
  onSelect: (conversationId: string) => void;
  /** Nome del cliente collegato per id cliente (per etichetta e ricerca). */
  clientNames: Map<string, string>;
  loading?: boolean;
}

/**
 * Lista delle conversazioni di un canale Meta. Stessa gerarchia visiva della
 * lista chat WhatsApp (nome, ultimo evento, pallino "da rispondere") e stesse
 * voci del menu di riga (fissa, archivia, silenzia) su whatsapp_chat_meta con
 * chiave 'meta:<id>'. Le righe WhatsApp leggono tutto dal loro store, per questo
 * la riga e' un componente a parte (MetaConversationRow).
 */
export function MetaConversationList({ channel, conversations, selectedId, onSelect, clientNames, loading }: MetaConversationListProps) {
  const [query, setQuery] = useState('');
  const Icon = channel === 'instagram' ? Instagram : Facebook;
  const loaded = useMetaMessagingStore((s) => s.loaded);
  const reads = useMetaMessagingStore((s) => s.reads);
  const isUnread = useMetaMessagingStore((s) => s.isUnread);
  const markRead = useMetaMessagingStore((s) => s.markRead);
  void reads; // ricalcolo delle righe quando cambiano le letture
  const { organizationId } = useUserData();
  const chatMeta = useWhatsAppChatMetaStore((s) => s.chatMeta);
  const fetchChatMeta = useWhatsAppChatMetaStore((s) => s.fetchChatMeta);
  const rowPrefs = useChatRowPrefs(organizationId);
  const [showArchived, setShowArchived] = useState(false);

  // Fissa/archivia/silenzia: righe di whatsapp_chat_meta con chiave 'meta:<id>'.
  const keysSignature = useMemo(() => conversations.map((c) => metaPrefsKey(c.id)).join('|'), [conversations]);
  useEffect(() => {
    if (!organizationId || !keysSignature) return;
    void fetchChatMeta(keysSignature.split('|'), organizationId);
  }, [keysSignature, organizationId, fetchChatMeta]);

  // Come WhatsApp: fissate in cima, archiviate in una sezione a parte (la ricerca le mostra tutte).
  const { rows, archivedCount } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const { visible, archived } = arrangeMetaConversations(conversations, (key) => chatMeta.get(key));
    const source = q ? [...visible, ...archived] : showArchived ? archived : visible;
    const items = source.map((c) => ({
      conversation: c,
      label: conversationLabel(c, c.client_id ? clientNames.get(c.client_id) : null),
    }));
    const filtered = !q ? items : items.filter(({ conversation, label }) =>
      label.toLowerCase().includes(q) || conversation.external_contact_id.toLowerCase().includes(q)
    );
    return { rows: filtered, archivedCount: archived.length };
  }, [conversations, clientNames, query, chatMeta, showArchived]);

  useEffect(() => {
    if (showArchived && archivedCount === 0) setShowArchived(false);
  }, [showArchived, archivedCount]);

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 pt-3 pb-2 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <Icon className="h-4 w-4 text-[#787774]" />
          <h3 className="text-sm font-semibold text-[#37352F] dark:text-white">{CHANNEL_LABEL[channel]}</h3>
          <span className="text-xs text-[#a4a4a0]">{conversations.length}</span>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#a4a4a0]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca conversazione"
            className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-transparent focus:border-[#2383e2] focus:outline-none text-[#37352F] dark:text-white placeholder:text-[#a4a4a0]"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {!query && (showArchived || archivedCount > 0) && (
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#37352F] dark:text-white border-b border-[rgba(55,53,47,0.06)] dark:border-white/5 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5"
          >
            {showArchived ? <ArrowLeft className="h-4 w-4 text-[#787774]" /> : <Archive className="h-4 w-4 text-[#787774]" />}
            <span className="flex-1 text-left">{showArchived ? 'Torna a tutte le conversazioni' : 'Archiviate'}</span>
            {!showArchived && <span className="text-xs text-[#787774]">{archivedCount}</span>}
          </button>
        )}
        {!loaded && conversations.length === 0 && (
          <div className="animate-pulse" aria-label="Caricamento conversazioni">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="px-3 py-2.5 flex items-center gap-3 border-b border-[rgba(55,53,47,0.06)] dark:border-white/5">
                <div className="h-9 w-9 rounded-full bg-[rgba(55,53,47,0.08)] dark:bg-white/10" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-2/5 rounded bg-[rgba(55,53,47,0.08)] dark:bg-white/10" />
                  <div className="h-2.5 w-3/5 rounded bg-[rgba(55,53,47,0.06)] dark:bg-white/5" />
                </div>
              </div>
            ))}
          </div>
        )}
        {loaded && !loading && rows.length === 0 && (
          <div className="px-4 py-10 text-center">
            <Icon className="h-8 w-8 text-[#c9c9c5] mx-auto mb-2" />
            <p className="text-sm text-[#787774]">
              {query ? 'Nessuna conversazione trovata' : showArchived ? 'Nessuna conversazione archiviata' : `Nessun messaggio ${CHANNEL_LABEL[channel]} ricevuto finora`}
            </p>
          </div>
        )}
        {rows.map(({ conversation, label }) => {
          const key = metaPrefsKey(conversation.id);
          const chat = metaChatLike(conversation);
          return (
            <MetaConversationRow
              key={conversation.id}
              conversation={conversation}
              label={label}
              active={conversation.id === selectedId}
              unread={isUnread(conversation)}
              onSelect={onSelect}
              onMarkAsRead={() => void markRead(conversation.id)}
              prefs={rowPrefs.stateOf(key, chat)}
              prefsAvailable={rowPrefs.prefsAvailable}
              onTogglePin={() => rowPrefs.togglePin(key, chat)}
              onToggleArchive={() => rowPrefs.toggleArchive(key, chat)}
              onMute={(opt) => rowPrefs.mute(key, opt)}
            />
          );
        })}
      </div>
    </div>
  );
}
